import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkillBadge } from "./SkillBadge";
import { Trophy, Target, Percent, Calendar, Building2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  calculateProfileCompletion,
  isHomeClubMissing,
  type ProfileCompletionFields,
} from "@workspace/profile-completion";

export interface PlayerProfileProps {
  name: string;
  avatar?: string;
  level: number;
  globalRank?: number | null;
  nationalRank?: number | null;
  nationalRankBest?: number | null;
  playerCountry?: string | null;
  matchesPlayed: number;
  winRate: number;
  memberSince: string;
  profileImageUrl?: string | null;
  profileData?: ProfileCompletionFields | null;
  homeClubName?: string | null;
  onAddHomeClub?: () => void;
}

export function PlayerProfile({
  name,
  avatar,
  level,
  globalRank,
  nationalRank,
  nationalRankBest,
  playerCountry,
  matchesPlayed,
  winRate,
  memberSince,
  profileImageUrl,
  profileData,
  homeClubName,
  onAddHomeClub,
}: PlayerProfileProps) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const displayAvatar = profileImageUrl || avatar;
  const profileForCalc: ProfileCompletionFields | null = profileData
    ? { ...profileData, homeClubName: profileData.homeClubName ?? homeClubName ?? null }
    : homeClubName
      ? { homeClubName }
      : null;
  const profileCompletion = calculateProfileCompletion({
    name,
    profileImageUrl,
    profile: profileForCalc,
  });
  const homeClubMissing = isHomeClubMissing({
    name,
    profileImageUrl,
    profile: profileForCalc,
  });

  return (
    <Card data-testid="card-player-profile">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="relative">
            <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
              <AvatarImage src={displayAvatar || undefined} alt={name} className="object-cover" />
              <AvatarFallback className="text-2xl font-bold bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div
              className="absolute -bottom-2 left-1/2 -translate-x-1/2"
              title={`Perfil ${profileCompletion}% completado`}
              data-testid="ring-profile-completion"
            >
              <svg className="w-12 h-6" viewBox="0 0 48 24">
                <path
                  d="M4 20 A20 20 0 0 1 44 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  className="text-muted"
                />
                <path
                  d="M4 20 A20 20 0 0 1 44 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${profileCompletion * 0.628} 100`}
                  className={profileCompletion === 100 ? "text-green-500" : "text-primary"}
                />
              </svg>
              <span
                className="absolute inset-0 flex items-center justify-center text-xs font-bold"
                data-testid="text-profile-completion"
              >
                {profileCompletion}%
              </span>
            </div>
          </div>
          <h2 className="mt-4 text-xl font-bold" data-testid="text-profile-name">
            {name}
          </h2>
          <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
            <SkillBadge level={level} size="lg" />
            <Badge variant="secondary">
              Pádel
            </Badge>
            {nationalRank != null && nationalRank <= 10 ? (
              <Badge
                style={{ backgroundColor: "#325e22", color: "#ffffff" }}
                data-testid="badge-top10-nacional"
              >
                Top {nationalRank} 🏆
              </Badge>
            ) : null}
          </div>
        </div>

        {homeClubMissing ? (
          <div
            className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10"
            data-testid="banner-add-home-club"
          >
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold">Añade tu club</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Completa tu perfil al 100% indicando dónde sueles jugar.
              </p>
              {onAddHomeClub ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 h-8"
                  onClick={onAddHomeClub}
                  data-testid="button-add-home-club"
                >
                  Añadir mi club
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Trophy className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Ranking Nacional</p>
              <p className="font-bold">{nationalRank != null ? `#${nationalRank.toLocaleString()}` : "-"}</p>
              {nationalRankBest != null && (
                <p className="text-[10px] text-primary/70 leading-snug mt-0.5">Mejor ranking: #{nationalRankBest.toLocaleString()}</p>
              )}
              {playerCountry && nationalRank == null && nationalRankBest == null && (
                <p className="text-[10px] text-primary/70 leading-snug mt-0.5">Registra tu primer partido</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Target className="h-4 w-4 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Partidos</p>
              <p className="font-bold">{matchesPlayed}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Percent className="h-4 w-4 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">% Victorias</p>
              <p className="font-bold">{winRate}%</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="p-2 rounded-lg bg-violet-500/10">
              <Calendar className="h-4 w-4 text-violet-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Miembro Desde</p>
              <p className="font-bold text-sm">{memberSince}</p>
            </div>
          </div>
          {homeClubName ? (
            <div
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 col-span-2"
              data-testid="row-profile-home-club"
            >
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Building2 className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Club</p>
                <p
                  className="font-bold text-sm truncate"
                  data-testid="text-profile-home-club"
                >
                  {homeClubName}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
